import { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import TruckPopup from "../TruckPopup/TruckPopup";
import TrailerPopup from "../TrailerPopup/TrailerPopup";
import { fetchAssets } from "../SalePopup/SalePopup";
import {
  makeFieldConfigDynamic,
  saleInitialValues,
  updateSaleFieldConfiguration,
} from "../../../formConfigs/Fleet/saleFieldConfig";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import dayjs from "dayjs";

interface UpdateSalePopupProps {
  sale: any;
}

const UpdateSalePopup = ({ sale }: UpdateSalePopupProps) => {
  const [open, setOpen] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<string>("");
  const [initialValues, setInitialValues] = useState(saleInitialValues);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: assetData } = useQuery({
    queryKey: ["assets", selectedAssetType],
    queryFn: () => fetchAssets(selectedAssetType, token),
    enabled: open,
  });

  useEffect(() => {
    if (open && sale && assetData) {
      setInitialValues({
        assetAffected: sale.truckId ? "Truck" : "Trailer",
        assetNumber: assetData.find(
          (a: any) => a.id === (sale.truckId || sale.trailerId)
        ),
        fmvDate: dayjs(sale.fmvDate).format("YYYY-MM-DD"),
        saleDate: sale.saleDate
          ? dayjs(sale.saleDate).format("YYYY-MM-DD")
          : null,
        forSale: sale.forSale,
        fairMarketValue: sale.fairMarketValue,
        fmvUsage: sale.fmvUsage,
        saleStatus: sale.saleStatus,
        isSaleFeatured: sale.isSaleFeatured,
      });
    }
  }, [open, sale, assetData]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/sale`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["sales"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update sale");
    },
  });

  const submitHandler = (data: any) => {
    const submitData = {
      ...data,
      id: sale.id,
      truckId: selectedAssetType === "Truck" ? getId(data.assetNumber) : null,
      trailerId:
        selectedAssetType === "Trailer" ? getId(data.assetNumber) : null,
    };
    delete submitData.assetAffected;
    delete submitData.assetNumber;
    updateMutation.mutate(submitData);
  };

  const onValuesChange = (values: any) => {
    const assetType = values.assetAffected;
    setSelectedAssetType(assetType);
  };

  const dynamicFields = makeFieldConfigDynamic(
    updateSaleFieldConfiguration,
    setSelectedAssetType,
    selectedAssetType
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        title="Update Sale"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={{ assets: assetData }}
        loading={updateMutation.isPending}
        onValuesChange={onValuesChange}
        fieldComponents={{
          assetNumber: (
            <Box display="flex" justifyContent="center" alignItems="center">
              {selectedAssetType === "Trailer" ? (
                <TrailerPopup />
              ) : (
                <TruckPopup />
              )}
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Sale updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateSalePopup;
